import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Sparkles, ArrowLeft, Mail, Lock, User as UserIcon, Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

export default function LoginPage({ mode = "login" }) {
  const isRegister = mode === "register";
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const from = location.state?.from || "/";

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = isRegister ? await register(email, password, name) : await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success(isRegister ? "Konto utworzone! ✨" : "Witaj z powrotem!");
      const dest = res.user?.role === "admin" ? "/admin" : from;
      navigate(dest, { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  const onGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen pt-32 md:pt-40 pb-20 relative overflow-hidden" data-testid={isRegister ? "register-page" : "login-page"}>
      <div className="aurora" style={{ width: 520, height: 520, background: "#FDE68A", top: "5%", left: "-8%" }} />
      <div className="aurora" style={{ width: 480, height: 480, background: "#BAE6FD", bottom: "5%", right: "-8%" }} />

      {/* Floating balloons */}
      {[0, 1, 2, 3, 4].map((i) => (
        <motion.span
          key={i}
          className="absolute text-4xl pointer-events-none"
          style={{ left: `${10 + i * 18}%`, top: `${15 + (i % 3) * 20}%` }}
          animate={{ y: [0, -20, 0], rotate: [-6, 6, -6] }}
          transition={{ duration: 5 + i, repeat: Infinity, ease: "easeInOut", delay: i * 0.4 }}
        >
          {["🎈", "☁️", "⭐", "✨", "🎀"][i]}
        </motion.span>
      ))}

      <div className="max-w-md mx-auto px-6 relative">
        <Link to="/" className="inline-flex items-center gap-2 chip !bg-white/80" data-testid="auth-back">
          <ArrowLeft size={14} /> Strona główna
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 rounded-[2rem] bg-white border border-[#BAE6FD] p-8 md:p-10 shadow-[0_20px_60px_-20px_rgba(11,42,85,0.25)]"
        >
          <span className="chip"><Sparkles size={12} /> {isRegister ? "Rejestracja" : "Logowanie"}</span>
          <h1 className="font-display font-semibold text-[#051937] text-3xl md:text-4xl mt-4 leading-tight">
            {isRegister ? (
              <>Cześć! Zacznijmy <em className="not-italic text-[#60A5FA]">przygodę.</em></>
            ) : (
              <>Witaj w <em className="not-italic text-[#60A5FA]">bajkowym świecie.</em></>
            )}
          </h1>

          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            {isRegister && (
              <div className="relative">
                <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-[#0b2a55]/50" size={18} />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Twoje imię (opcjonalnie)"
                  data-testid="auth-name"
                  className="w-full rounded-full bg-[#F4FAFF] border border-[#BAE6FD] pl-12 pr-5 py-3.5 text-[#051937] placeholder:text-[#0b2a55]/40 outline-none focus:border-[#60A5FA]"
                />
              </div>
            )}
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-[#0b2a55]/50" size={18} />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="E-mail"
                data-testid="auth-email"
                className="w-full rounded-full bg-[#F4FAFF] border border-[#BAE6FD] pl-12 pr-5 py-3.5 text-[#051937] placeholder:text-[#0b2a55]/40 outline-none focus:border-[#60A5FA]"
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-[#0b2a55]/50" size={18} />
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Hasło (min. 6 znaków)"
                data-testid="auth-password"
                className="w-full rounded-full bg-[#F4FAFF] border border-[#BAE6FD] pl-12 pr-5 py-3.5 text-[#051937] placeholder:text-[#0b2a55]/40 outline-none focus:border-[#60A5FA]"
              />
            </div>

            <motion.button
              type="submit"
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="auth-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#051937] hover:bg-[#0b2a55] disabled:opacity-70 text-white px-6 py-4 font-semibold shadow-[0_20px_50px_-20px_rgba(11,42,85,0.6)]"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={16} />}
              {isRegister ? "Załóż konto" : "Zaloguj się"}
            </motion.button>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-[#BAE6FD]" />
            <span className="text-xs uppercase tracking-[0.16em] font-bold text-[#0b2a55]/50">albo</span>
            <div className="flex-1 h-px bg-[#BAE6FD]" />
          </div>

          <button
            onClick={onGoogle}
            data-testid="auth-google"
            className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-white border-2 border-[#BAE6FD] hover:border-[#60A5FA] hover:-translate-y-0.5 transition-all text-[#051937] px-6 py-3.5 font-semibold"
          >
            <svg width="20" height="20" viewBox="0 0 20 20"><path fill="#4285F4" d="M19.6 10.23c0-.68-.06-1.36-.19-2H10v3.8h5.4c-.23 1.24-.94 2.3-2 3v2.5h3.24c1.9-1.75 2.96-4.33 2.96-7.3z"/><path fill="#34A853" d="M10 20c2.7 0 4.96-.9 6.64-2.44l-3.24-2.5c-.9.6-2.04.96-3.4.96-2.62 0-4.84-1.77-5.63-4.14H1v2.6C2.68 17.9 6.1 20 10 20z"/><path fill="#FBBC05" d="M4.37 11.9c-.2-.6-.32-1.24-.32-1.9s.12-1.3.32-1.9V5.5H1c-.68 1.36-1.06 2.9-1.06 4.5s.38 3.14 1.06 4.5l3.37-2.6z"/><path fill="#EA4335" d="M10 3.96c1.47 0 2.8.5 3.84 1.5l2.88-2.88C15 .96 12.7 0 10 0 6.1 0 2.68 2.1 1 5.5l3.37 2.6C5.16 5.73 7.38 3.96 10 3.96z"/></svg>
            Zaloguj przez Google
          </button>

          <p className="mt-8 text-center text-sm text-[#0b2a55]/70">
            {isRegister ? "Masz już konto?" : "Nie masz jeszcze konta?"}{" "}
            <Link
              to={isRegister ? "/login" : "/register"}
              className="font-semibold text-[#60A5FA] animated-underline"
              data-testid="auth-switch"
            >
              {isRegister ? "Zaloguj się" : "Zarejestruj się"}
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
